import type {
  Dispatch,
  FC,
  MutableRefObject,
  ReactElement,
  SetStateAction,
} from "react"
import { memo, useState } from "react"

import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  useFloating,
} from "@floating-ui/react"
import { IoIosArrowDown } from "react-icons/io"
import type { FlattenSimpleInterpolation } from "styled-components"
import styled from "styled-components"

import { BasicDropdownItem, SearchBar } from "./DropdownHelpers"
import { useOutsideClick } from "../../utils/useOutsideClick"

export type DropdownProps = {
  SelectedOption: FC
  wrapperStyle?: FlattenSimpleInterpolation
  listStyle?: FlattenSimpleInterpolation
  Label?: FC
  disabled?: boolean
  alwaysShowSearch?: boolean
  className?: string
  onSearchChange?: (value: string) => void
  Options: (JSX.Element | null)[]
  NullableOption?: JSX.Element | null
}

const DropdownInternal = ({
  SelectedOption,
  wrapperStyle,
  className,
  listStyle,
  Label = () => null,
  disabled = false,
  alwaysShowSearch = false,
  Options,
  onSearchChange,
  NullableOption = null,
}: DropdownProps): ReactElement => {
  const [open, setOpen] = useState<boolean>(false)
  const [debouncedInput, setDebouncedInput] = useState<string>(``)
  const { refs, strategy, x, y } = useFloating({
    open,
    whileElementsMounted: autoUpdate,
    strategy: `absolute`,
    placement: `bottom`,
    middleware: [
      offset({
        mainAxis: 10,
      }),
      flip({
        rootBoundary: `viewport`,
        crossAxis: false,
      }),
    ],
  })

  useOutsideClick({
    providedRef: refs.reference as MutableRefObject<HTMLElement | null>,
    providedRef2: refs.floating,
    action: () => setOpen(false),
  })

  return (
    <Wrapper
      data-role="dropdown-container"
      className={className}
      css={wrapperStyle}
      data-disabled={disabled}
    >
      <Label />
      <div
        data-role="dropdown-wrapper"
        ref={refs.setReference}
        onClick={() => {
          if (disabled) return
          setOpen(!open)
          setDebouncedInput(``)
        }}
      >
        <div data-role="selected-option-wrapper">
          <SelectedOption />
        </div>
        <div data-role="arrow">
          <div data-role="spacer" />
          <IoIosArrowDown
            data-role="arrow-icon"
            style={{
              transform: `rotate(${open ? `180deg` : `0deg`})`,
            }}
          />
        </div>
      </div>
      {open && (
        <FloatingPortal>
          <List
            ref={refs.setFloating}
            style={{
              position: strategy,
              left: x ?? 0,
              top: y ?? 0,
              width: refs.reference?.current?.getBoundingClientRect().width ?? 0,
            }}
            css={listStyle}
            onClick={() => setOpen(false)}
          >
            <SearchBar
              onSearchChange={onSearchChange}
              setDebouncedInput={setDebouncedInput}
              optionsLength={Options.length}
              alwaysShowSearch={alwaysShowSearch}
            />
            <DropdownOptions
              Options={Options}
              setOpen={setOpen}
              NullableOption={NullableOption}
              input={debouncedInput}
            />
          </List>
        </FloatingPortal>
      )}
    </Wrapper>
  )
}
const DropdownComponent = memo(DropdownInternal)

const DropdownOptionsInternal: FC<{
  Options: (JSX.Element | null)[]
  setOpen: Dispatch<SetStateAction<boolean>>
  NullableOption: JSX.Element | null
  input: string
}> = ({ Options, setOpen, NullableOption, input }) => {
  const displayNoOptions = () => {
    return (
      <>
        <div
          data-role="dropdown-item"
          onClick={() => {
            setOpen(false)
          }}
        >
          <div>
            <p>No Options</p>
          </div>
        </div>
      </>
    )
  }

  if (Options.length === 0) {
    return <> {displayNoOptions()} </>
  }

  const FilteredOptions = Options.filter((option) => {
    if (option?.props?.label1) {
      return [option.props.label1, option.props.label2]
        .filter(Boolean)
        .join(` `)
        .toLowerCase()
        .includes(input.toLowerCase())
    }

    return false
  })

  if (FilteredOptions.length > 0 || NullableOption) {
    const sortedOptions = input
      ? [...FilteredOptions].sort((a, b) => {
          if (a?.props?.label1 && b?.props?.label1) {
            return a.props.label1 > b.props.label1 ? 1 : -1
          }

          return 0
        })
      : FilteredOptions

    return <>{[NullableOption, ...sortedOptions]}</>
  }

  return <> {displayNoOptions()} </>
}
const DropdownOptions = memo(DropdownOptionsInternal)

export const Dropdown = { Item: BasicDropdownItem, Container: DropdownComponent }

const Wrapper = styled.div<{ css?: FlattenSimpleInterpolation }>`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  position: relative;
  flex-shrink: 1;
  > label {
    font-size: var(--step--2);
    font-weight: 600;
    text-transform: capitalize;
    color: var(--queen-blue);
  }
  &[data-disabled="true"] {
    &,
    * {
      cursor: not-allowed !important;
    }
    opacity: 0.5;
  }
  [data-role="dropdown-wrapper"] {
    display: flex;
    cursor: pointer;
    justify-content: space-between;
    width: 100%;
    flex-shrink: 1;
    align-items: center;
    border: 1px solid #c4c4c4;
    background-color: white;
    :hover {
      border: 1px solid var(--blue);
    }
    padding: 0 10px;
    border-radius: 6px;
    height: 50px;
    width: 100%;
  }
  [data-role="arrow"] {
    display: flex;
    height: 100%;
    width: fit-content;
    justify-content: space-between;
    align-items: center;
    position: relative;
    [data-role="spacer"] {
      flex-shrink: 0;
      width: 0px;
      height: 50%;
      border-left: 1px solid var(--queen-blue);
    }
    [data-role="arrow-icon"] {
      user-select: none;
      margin-left: 10px;
      font-size: var(--step--1);
      color: var(--queen-blue);
    }
  }
  [data-role="selected-option-wrapper"] {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 1;
    gap: 10px;
    position: relative;
    /* padding: 0 10px; */
    width: calc(100% - 40px);
    > p {
      position: relative;
      font-size: var(--step--1);
      color: var(--queen-blue);
      text-transform: capitalize;
      width: 100%;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    > span {
      width: fit-content;
      font-size: var(--step--1);
      white-space: nowrap;
    }
  }
  ${({ css }) => css}
`

const List = styled.div<{ css?: FlattenSimpleInterpolation }>`
  z-index: 9999;
  background-color: white;
  height: fit-content;
  max-height: 302px;
  overflow-y: auto;
  border: 1px solid var(--blue);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  justify-content: space-between;
  border-radius: 4px;
  width: 100%;
  [data-role="dropdown-search"] {
    display: flex;
    align-items: center;
    gap: 5px;
    top: 0;
    left: 0;
    position: sticky;
    width: 100%;
    padding: 10px;
    backdrop-filter: blur(10px);
    background-color: rgba(255, 255, 255, 0.5);
    border-bottom: 1px solid var(--blue);
    cursor: text;
    svg {
      font-size: var(--step--2);
      color: var(--queen-blue);
      width: fit-content;
      height: fit-content;
    }
    > input[type="text"] {
      &,
      &::placeholder {
        color: var(--queen-blue);
        font-size: var(--step--2);
      }
      width: 100%;
      height: 100%;
    }
  }
  [data-role="dropdown-item"] {
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    padding: 15px 15px;
    &:not(:first-of-type) {
      border-top: 1px solid #006aff35;
    }
    &:hover {
      background-color: var(--blue);
      > div p,
      > div span:not(.base-chip) {
        color: white;
      }
      > div .base-chip {
        color: #16a34a; /* text-green-600 */
        background: #ffffff;
        border-color: #22c55e; /* green-500 */
      }
      > [data-role="check-wrapper"] {
        > svg {
          color: white;
        }
      }
    }
    > div {
      display: flex;
      width: 100%;
      gap: 10px;
      align-items: center;
      justify-content: space-between;
      padding-right: clamp(5px, 2%, 10px);
      > p {
        color: var(--queen-blue);
        font-size: var(--step--1);
        text-transform: capitalize;
      }
      > p + p {
        border-top: 1px solid rgba(81, 102, 131, 1);
      }
      > span {
        font-size: var(--step--2);
        color: var(--queen-blue);
        margin-left: auto;
      }
    }
    > [data-role="check-wrapper"] {
      width: var(--step-0);
      height: var(--step-0);
      > svg {
        margin-left: auto;
        font-size: var(--step-0);
        color: var(--blue);
      }
    }
  }
  ${({ css }) => css}
`
